import { ArrowLeft, ArrowRight, Save, Send } from 'lucide-react';
import { useRef } from 'react';
import { useI18n } from '../../../i18n/I18nProvider';
import { useBottomLayer } from '../../../lib/hooks';
import { Button } from '../../../ui/Button';
import styles from '../Sales.module.css';

/**
 * The step bar docked at the bottom (BLOCK-1-REDESIGN §A.2): the save state, «رجوع» (steps 2–4),
 * «احفظ وأكمل لاحقًا» (every step), then «متابعة إلى …» (steps 1–3) or, on the review step only,
 * «أصدِر وأرسِل للمصلحة». On phones back and save become named icons.
 */
export function StepBar({
  saveState,
  isSaved,
  backTo,
  nextTo,
  onBack,
  onNext,
  canSave,
  isSaving,
  onSave,
  canIssue,
  onIssue,
}: {
  saveState: string;
  isSaved: boolean;
  /** Name of the previous step, or null on the first step. */
  backTo: string | null;
  /** Name of the next step, or null on the review step. */
  nextTo: string | null;
  onBack: () => void;
  onNext: () => void;
  canSave: boolean;
  isSaving: boolean;
  onSave: () => void;
  /** Shown on the review step only, with the issue and submit permissions. */
  canIssue: boolean;
  onIssue: () => void;
}) {
  const { t } = useI18n();
  const barRef = useRef<HTMLDivElement>(null);
  useBottomLayer(barRef);
  return (
    <div ref={barRef} className={styles.actionBar} data-testid="action-bar">
      <span className={styles.actionStatus} data-testid="save-state">
        <span className={`${styles.dot} ${isSaved ? styles.dotSaved : ''}`} />
        {saveState}
      </span>
      <div className={styles.stepActions}>
        {backTo ? (
          <Button
            variant="ghost"
            icon={<ArrowLeft size={16} className="flip-rtl" aria-hidden="true" />}
            onPress={onBack}
            testId="step-back"
            aria-label={t.editor.back}
            className={styles.stepBack}
          >
            <span className={styles.stepBarText}>{t.editor.back}</span>
          </Button>
        ) : null}
        {canSave ? (
          <Button
            variant="secondary"
            icon={<Save size={16} aria-hidden="true" />}
            isPending={isSaving}
            onPress={onSave}
            testId="save-draft"
            aria-label={t.editor.saveDraft}
            className={styles.stepSave}
          >
            <span className={styles.stepBarText}>{t.editor.saveDraft}</span>
          </Button>
        ) : null}
        {nextTo ? (
          <Button variant="primary" onPress={onNext} testId="step-next" className={styles.stepNext}>
            {t.editor.next(nextTo)}
            <ArrowRight size={16} className="flip-rtl" aria-hidden="true" />
          </Button>
        ) : canIssue ? (
          <Button
            variant="primary"
            icon={<Send size={16} aria-hidden="true" />}
            isDisabled={isSaving}
            onPress={onIssue}
            testId="issue-send"
            className={styles.stepNext}
          >
            {t.editor.issueSend}
          </Button>
        ) : null}
      </div>
    </div>
  );
}
